import { useState } from 'react';

export type SidebarSide = 'left' | 'right';

type PerSide = Record<SidebarSide, boolean>;

const BOTH_CLOSED: PerSide = { left: false, right: false };

export interface SidebarLayout {
  /** From md: the side panel is expanded. Starts open and outlives any trip through compact. */
  panelOpen: PerSide;
  /** Compact: the side sheet is showing. Starts closed and closes whenever the screen leaves compact. */
  sheetOpen: PerSide;
  /** What the header toggle shows for each side at the current width. */
  open: PerSide;
  /** The header toggle: the sheet on compact, the panel from md. */
  toggle: (side: SidebarSide) => void;
  /** Opens one side at the current width, as the empty state's Open investigations does. */
  show: (side: SidebarSide) => void;
  /** Closes the sheet on one side; a no-op from md, where the panel stays as it was. */
  closeSheet: (side: SidebarSide) => void;
  /** The wide panel was dragged shut or open, so the toggle follows it. */
  setPanelOpen: (side: SidebarSide, open: boolean) => void;
}

/**
 * The open state of the investigation screen's two sidebars. A wide panel and a compact sheet are
 * separate states, so turning a tablet neither opens a sheet nor reopens a panel the operator closed.
 */
export function useSidebarLayout(isCompact: boolean): SidebarLayout {
  const [panelOpen, setPanelOpenState] = useState<PerSide>({ left: true, right: true });
  const [sheetOpen, setSheetOpen] = useState<PerSide>(BOTH_CLOSED);
  const [wasCompact, setWasCompact] = useState(isCompact);

  // Adjusted while rendering rather than in an effect, so no frame ever draws a sheet from md.
  if (wasCompact !== isCompact) {
    setWasCompact(isCompact);
    if (!isCompact) setSheetOpen(BOTH_CLOSED);
  }

  const setPanelOpen = (side: SidebarSide, open: boolean) => {
    setPanelOpenState(prev => (prev[side] === open ? prev : { ...prev, [side]: open }));
  };

  // One sheet at a time: both together would cover the chat with two layers of scrim.
  const setSheet = (side: SidebarSide, open: boolean) => {
    setSheetOpen(prev => (open ? { ...BOTH_CLOSED, [side]: true } : { ...prev, [side]: false }));
  };

  const toggle = (side: SidebarSide) => {
    if (isCompact) setSheet(side, !sheetOpen[side]);
    else setPanelOpen(side, !panelOpen[side]);
  };

  const show = (side: SidebarSide) => {
    if (isCompact) setSheet(side, true);
    else setPanelOpen(side, true);
  };

  const closeSheet = (side: SidebarSide) => setSheet(side, false);

  return {
    panelOpen,
    sheetOpen,
    open: isCompact ? sheetOpen : panelOpen,
    toggle,
    show,
    closeSheet,
    setPanelOpen,
  };
}
